import {
  AdminBookingStateEnum,
  FolderStatusEnum,
  TravelerKindEnum,
} from '@koob/enums';
import { toDateString, yearsSince } from '@koob/dates';

export const calculateAge = (birthdate, referenceDate = new Date()) => {
  const today = new Date(referenceDate);
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

const ADULT_MIN_AGE = 18;

/**
 * Whether a folder traveler occupies a child seat.
 *
 * `kind` is authoritative when the record carries one: KD-3546 lets a folder
 * hold placeholders with no birthdate at all, so the age alone can no longer
 * decide. Records saved before `kind` existed still fall back on the age, and
 * an undated legacy record counts as an adult — the same default the API
 * applies in classifyTravelerKind.
 */
export const isFolderChildTraveler = (traveler, referenceDate = new Date()) => {
  if (traveler?.kind) {
    return traveler.kind !== TravelerKindEnum.ADULT;
  }
  if (!traveler?.birthdate) {
    return false;
  }
  const age = calculateAge(traveler.birthdate, referenceDate);

  return Number.isFinite(age) && age < ADULT_MIN_AGE;
};

/**
 * A folder traveler as the travelers table shows it (KD-3842): age and
 * Adult / Child at the trip departure date (the stored kind decides when there
 * is no birthdate), and whether the passport expires before the return date.
 * Supplier-specific age rules are checked at booking time, not here.
 */
export const getFolderTravelerRow = (
  traveler,
  { departureDate = null, returnDate = null } = {},
) => {
  const referenceDate = departureDate ? new Date(departureDate) : new Date();
  const age = traveler?.birthdate
    ? calculateAge(traveler.birthdate, referenceDate)
    : null;
  const isChild =
    age !== null && Number.isFinite(age)
      ? age < ADULT_MIN_AGE
      : isFolderChildTraveler(traveler, referenceDate);
  const expiresBeforeReturn = Boolean(
    returnDate &&
    traveler?.passportExpirationDate &&
    new Date(traveler.passportExpirationDate) < new Date(returnDate),
  );

  return {
    traveler,
    name: `${traveler?.firstName ?? ''} ${traveler?.lastName ?? ''}`.trim(),
    ageAtDeparture: Number.isFinite(age) ? age : null,
    isChild,
    expiresBeforeReturn,
  };
};

/**
 * Order travelers the way the folder does: by position first, then in the order
 * the API returned them — Array.prototype.sort is stable, so records predating
 * the field keep their relative rank behind the positioned ones.
 */
const byFolderPosition = (a, b) => {
  const aPosition = a?.position ?? null;
  const bPosition = b?.position ?? null;

  if (aPosition !== null && bPosition !== null) return aPosition - bPosition;
  if (aPosition !== null) return -1;
  if (bPosition !== null) return 1;

  return 0;
};

/**
 * The folder's main guest (KD-3842): the traveler chosen through
 * `mainTravelerId`, otherwise the first adult by position, otherwise null.
 * A child is never main guest. It does not depend on the bookings, so a folder
 * with travelers but no booking has one too.
 */
export const getFolderMainGuest = (folder) => {
  const travelers = [...(folder?.travelers ?? [])].sort(byFolderPosition);
  const adults = travelers.filter(
    (traveler) => !isFolderChildTraveler(traveler),
  );
  const chosen = adults.find(
    (traveler) =>
      folder?.mainTravelerId != null &&
      String(traveler?.id) === String(folder.mainTravelerId),
  );

  return chosen ?? adults[0] ?? null;
};

/**
 * Read a folder traveler field for a form that has not been filled yet.
 *
 * A KD-3546 placeholder stores every unfilled identity field as null, and the
 * booking pax forms validate with Yup `string()` / `date()` schemas: null fails
 * their type check and paints the whole form red before the user has typed
 * anything. An unset value has to stay undefined.
 */
export const blankToUndefined = (value) =>
  value === null || value === '' ? undefined : value;

/**
 * Split a client folder's travelers into the adult and child queues the booking
 * tunnel fills its room seats from.
 *
 * Matching used to recompute the age from the birthdate, which dropped every
 * KD-3546 placeholder (no birthdate yet) out of both queues: the seat then kept
 * its generated id, the tunnel posted a traveler without a folder id and the API
 * appended a brand new folder traveler instead of completing the placeholder.
 *
 * KD-3842: the folder's main guest heads the adult queue, so the tunnel seats
 * them first in the first room and the booking makes them room leader. Without
 * a chosen main guest, the first adult by position already stands in.
 */
export const splitFolderTravelers = (
  travelers,
  referenceDate = new Date(),
  mainTravelerId = null,
) => {
  const adults = [];
  const children = [];

  [...(travelers ?? [])].sort(byFolderPosition).forEach((traveler) => {
    if (isFolderChildTraveler(traveler, referenceDate)) {
      children.push(traveler);
    } else {
      adults.push(traveler);
    }
  });

  const mainGuestIndex =
    mainTravelerId == null
      ? -1
      : adults.findIndex(
          (traveler) => String(traveler?.id) === String(mainTravelerId),
        );
  if (mainGuestIndex > 0) {
    adults.unshift(...adults.splice(mainGuestIndex, 1));
  }

  return { adults, children };
};

/**
 * The search rooms with the client folder's children birthdates, or null when
 * the search already prices the folder's children at the right ages.
 *
 * Only the age at check-in sets the price, and a child can sit in any room, so
 * the ages are compared as a set: two different birthdates giving the same age
 * are not a mismatch, nor is the folder's children spread over the rooms in
 * another order. The check stays silent when it cannot be sure —
 * no check-in date, a folder child without a birthdate, or a different number
 * of children (the API rejects that booking on its own).
 */
export const roomsWithFolderChildBirthdates = (rooms, folder, checkInDate) => {
  const { children } = splitFolderTravelers(folder?.travelers, checkInDate);
  const searched = (rooms ?? []).flatMap(
    (room) => room?.childrenBirthdates ?? [],
  );

  if (
    !checkInDate ||
    children.length === 0 ||
    children.length !== searched.length ||
    [...children.map((child) => child.birthdate), ...searched].some((d) => !d)
  ) {
    return null;
  }

  const agesAtCheckIn = (dates) =>
    dates.map((date) => yearsSince(date, checkInDate)).sort((a, b) => a - b);
  const folderAges = agesAtCheckIn(children.map((child) => child.birthdate));
  const searchedAges = agesAtCheckIn(searched);
  const differs = folderAges.some((age, index) => age !== searchedAges[index]);
  if (!differs) {
    return null;
  }

  const birthdates = children.map((child) => toDateString(child.birthdate));
  return rooms.map((room) => ({
    ...room,
    childrenBirthdates: birthdates.splice(
      0,
      room?.childrenBirthdates?.length ?? 0,
    ),
  }));
};

const folderChildPool = (folder, checkInDate) =>
  splitFolderTravelers(folder?.travelers, checkInDate)
    .children.map((child) => toDateString(child.birthdate))
    .filter(Boolean);

const takeFromPool = (pool, date) => {
  const index = pool.indexOf(toDateString(date));
  if (index !== -1) {
    pool.splice(index, 1);
  }
};

const seatsOf = (room) =>
  (room?.childrenBirthdates ?? []).slice(0, room?.children ?? 0);

/**
 * Rooms with their blank child seats filled from the folder's children, in
 * folder position order, each child dealt once. A filled seat keeps its date,
 * a seat past the room's `children` count is dropped, and `rooms` comes back
 * by reference when nothing moves.
 */
export const fillRoomsChildBirthdates = (rooms, folder, checkInDate) => {
  const pool = folderChildPool(folder, checkInDate);

  // Trimmed first: a dropped seat gives its child back to the pool.
  (rooms ?? []).flatMap(seatsOf).forEach((date) => takeFromPool(pool, date));

  let changed = false;
  const filled = (rooms ?? []).map((room) => {
    const current = room?.childrenBirthdates ?? [];
    const next = Array.from(
      { length: room?.children ?? 0 },
      (_, index) => current[index] ?? pool.shift(),
    );

    // The save button reads this length as the seats filled: never pad it.
    while (next.length && next[next.length - 1] == null) {
      next.pop();
    }
    if (
      next.length === current.length &&
      next.every((date, index) => date === current[index])
    ) {
      return room;
    }
    changed = true;
    return { ...room, childrenBirthdates: next };
  });

  return changed ? filled : rooms;
};

/**
 * The folder birthdates a child seat can still be given: the folder's children
 * in position order, minus the ones the other seats hold, plus the seat's own
 * date. Distinct dates only — two children born the same day read as one
 * choice, which stays offered while the folder still has one to give.
 */
export const folderChildBirthdateOptions = (
  rooms,
  folder,
  checkInDate,
  roomIndex,
  childIndex,
) => {
  const pool = folderChildPool(folder, checkInDate);

  (rooms ?? []).forEach((room, index) =>
    seatsOf(room).forEach((date, seatIndex) => {
      if (index !== roomIndex || seatIndex !== childIndex) {
        takeFromPool(pool, date);
      }
    }),
  );

  return [...new Set(pool)];
};

const CANCELLED_STATES = [
  AdminBookingStateEnum.CANCELLED,
  AdminBookingStateEnum.REFUSED,
  AdminBookingStateEnum.FAILED,
];

const withoutReplaced = (bookings) =>
  (bookings ?? []).filter(
    (booking) => booking.state !== AdminBookingStateEnum.REPLACED,
  );

/**
 * The folder status, ARCHIVED included (KD-3842). Archiving is the only stored
 * status: it hides the derived one, which getFolderBaseStatus still gives.
 */
export function getFolderStatus(folder) {
  if (folder?.archivedAt) return FolderStatusEnum.ARCHIVED;
  return getFolderBaseStatus(folder);
}

/**
 * Whether the folder can be archived: only New or Draft folders, i.e. holding
 * no booking and no booked trip. Same rule as the API (409 otherwise). The API
 * already leaves draft bookings out of `folder.bookings`.
 */
export function isFolderArchivable(folder) {
  return (
    !!folder &&
    !folder.archivedAt &&
    (folder.bookings ?? []).length === 0 &&
    (folder.trips ?? []).every((trip) => trip.draft)
  );
}

/**
 * "Cancel all bookings" is offered on Confirming and Confirmed folders only,
 * and never on an archived one (KD-3842).
 */
export function canCancelAllFolderBookings(folder) {
  return (
    !!folder &&
    !folder.archivedAt &&
    [FolderStatusEnum.CONFIRMING, FolderStatusEnum.CONFIRMED].includes(
      getFolderBaseStatus(folder),
    )
  );
}

// States a booking can still be cancelled from, by "Cancel all bookings".
const CANCELLABLE_BOOKING_STATES = [
  AdminBookingStateEnum.CONFIRMED,
  AdminBookingStateEnum.SENT,
  AdminBookingStateEnum.PRE_CONFIRMED,
];

/**
 * The hotel and experience bookings "Cancel all bookings" offers to cancel
 * (KD-3842: shared by the TO and DMC folder pages).
 */
export function getCancellableFolderBookings(bookings) {
  return (bookings ?? []).filter(
    (booking) =>
      CANCELLABLE_BOOKING_STATES.includes(booking.state) &&
      (booking.hotel || booking.experiencesBooked?.length > 0),
  );
}

/** The status derived from the bookings, ignoring archiving. */
export function getFolderBaseStatus(folder) {
  if (!folder) return FolderStatusEnum.NEW;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const bookings = withoutReplaced(folder.bookings);
  const trips = folder.trips ?? [];
  const hasDraftTrip = trips.some((trip) => trip.draft);

  if (bookings.length === 0) {
    return hasDraftTrip ? FolderStatusEnum.DRAFT : FolderStatusEnum.NEW;
  }

  const allCancelled = bookings.every((b) =>
    CANCELLED_STATES.includes(b.state),
  );

  if (allCancelled) {
    return FolderStatusEnum.CANCELLED;
  }

  const activeBookings = bookings.filter(
    (b) => !CANCELLED_STATES.includes(b.state),
  );

  const startDates = activeBookings
    .map((b) => (b.startAt ? new Date(b.startAt) : null))
    .filter(Boolean);
  const endDates = activeBookings
    .map((b) => (b.endAt ? new Date(b.endAt) : null))
    .filter(Boolean);

  const earliestStart = startDates.length
    ? new Date(Math.min(...startDates))
    : null;
  const latestEnd = endDates.length ? new Date(Math.max(...endDates)) : null;

  if (latestEnd) {
    latestEnd.setHours(0, 0, 0, 0);
  }
  if (earliestStart) {
    earliestStart.setHours(0, 0, 0, 0);
  }

  if (latestEnd && today > latestEnd) {
    return FolderStatusEnum.COMPLETED;
  }

  if (
    earliestStart &&
    latestEnd &&
    today >= earliestStart &&
    today <= latestEnd
  ) {
    return FolderStatusEnum.ON_SPOT;
  }

  if (
    activeBookings.every((b) => b.state === AdminBookingStateEnum.CONFIRMED)
  ) {
    return FolderStatusEnum.CONFIRMED;
  }

  return FolderStatusEnum.CONFIRMING;
}

export function getTripStatus(trip) {
  const bookings = withoutReplaced(trip?.statusBookings ?? trip?.bookings);

  if (trip?.draft || bookings.length === 0) {
    return FolderStatusEnum.DRAFT;
  }

  if (bookings.every((b) => CANCELLED_STATES.includes(b.state))) {
    return FolderStatusEnum.CANCELLED;
  }

  const activeBookings = bookings.filter(
    (b) => !CANCELLED_STATES.includes(b.state),
  );

  if (
    activeBookings.every((b) => b.state === AdminBookingStateEnum.CONFIRMED)
  ) {
    return FolderStatusEnum.CONFIRMED;
  }

  return FolderStatusEnum.CONFIRMING;
}

export const getRoomPax = (room) =>
  (room?.adults ?? 0) +
  (room?.childrenBirthdates?.length ?? room?.children ?? 0);

export const assignTravelersToRooms = (
  travelers,
  roomsComposition,
  roomCount,
) => {
  const adults = (travelers ?? []).filter(
    (traveler) => traveler?.kind === TravelerKindEnum.ADULT,
  );
  const children = (travelers ?? []).filter(
    (traveler) => traveler?.kind !== TravelerKindEnum.ADULT,
  );

  return Array.from({ length: roomCount }, (_, roomIndex) => {
    const room = roomsComposition?.[roomIndex] ?? {};
    const childCount = room.childrenBirthdates?.length ?? room.children ?? 0;
    return [
      ...adults.splice(0, room.adults ?? 0),
      ...children.splice(0, childCount),
    ];
  });
};

/**
 * A hotel room seat in the shape the booking pax form reads: `firstname`,
 * `lastname` and `expirationDate`. A seat carried over in the basket can still
 * hold the folder or API shape (`firstName`, `passportExpirationDate`), which the
 * form ignores: the child's names showed blank on a hotel booking while every
 * other field was filled. A value still missing is taken from the folder traveler
 * the seat points to; a value already typed is never overwritten.
 */
export const toRoomSeat = (seat, folderTravelers) => {
  const person = (folderTravelers ?? []).find(
    (traveler) => seat?.id != null && String(traveler?.id) === String(seat.id),
  );
  const firstFilled = (...values) =>
    values.map(blankToUndefined).find((value) => value !== undefined);

  return {
    ...seat,
    firstname: firstFilled(seat?.firstname, seat?.firstName, person?.firstName),
    lastname: firstFilled(seat?.lastname, seat?.lastName, person?.lastName),
    expirationDate: firstFilled(
      seat?.expirationDate,
      seat?.passportExpirationDate,
      person?.passportExpirationDate,
    ),
  };
};

const isChildTraveler = (traveler) => {
  const age = calculateAge(traveler?.birthdate);
  return Number.isFinite(age) && age < 18;
};

export const generateTravelerComposition = (customerFile) => {
  const travelers = customerFile?.travelers ?? [];
  const childrenPax = travelers.filter(isChildTraveler);
  const adults = travelers.length - childrenPax.length || 1;

  return {
    adults,
    children: childrenPax.length,
    childrenBirthdates: childrenPax.map((child) => child.birthdate),
    count: adults + childrenPax.length,
    firstAdultNationality: {},
  };
};

export const folderRoomsComposition = (folder) => {
  const rooms = folder?.roomsComposition;
  const travelers = folder?.travelers?.length ?? 0;
  const seats = (rooms ?? []).reduce(
    (total, room) => total + (room?.adults ?? 0) + (room?.children ?? 0),
    0,
  );

  if (
    Array.isArray(rooms) &&
    rooms.length > 0 &&
    (travelers === 0 || seats === travelers)
  ) {
    return rooms;
  }
  return [generateTravelerComposition(folder)];
};

export const folderPax = (folder) => {
  const rooms = folderRoomsComposition(folder);
  const adults = rooms.reduce((sum, r) => sum + (r.adults ?? 0), 0);
  const childrenBirthdates = rooms.flatMap((r) => r.childrenBirthdates ?? []);
  const children =
    childrenBirthdates.length > 0
      ? childrenBirthdates.length
      : rooms.reduce((sum, r) => sum + (r.children ?? 0), 0);

  return {
    adults,
    children,
    childrenBirthdates,
    count: adults + children,
    firstAdultNationality: {},
    roomsComposition: rooms,
  };
};
